import { api, getAccessToken, type StockImport, type StockImportContext, type StockImportLot, type StockImportSummary } from '../api';
import { callout, field, input, opt, openDialog, select, val } from '../dialog';
import { openPdfViewer } from '../dialogs/pdf-viewer';
import { printLabels } from '../dialogs/stock';
import { errorCard, esc, fmtDate, html, loading, pill, toast, type State } from '../ui';

/**
 * Receiving a delivery by reading its own paperwork.
 *
 * The screen exists because the reading cannot be trusted on its own. Three
 * readings of one delivery disagreed with each other — one put the price in
 * the part number — so every value here is shown beside the document and page
 * it was read from, everything is editable, and nothing reaches the store
 * until the button at the bottom is pressed.
 *
 * Same shape as reading a programme out of the manuals, for the same reason:
 * the reader is checking, not typing.
 */

const ROLE_LABEL: Record<string, string> = {
  INVOICE: 'Invoice',
  CONFORMITY: 'Certificate of conformity',
  RELEASE: 'Airworthiness release',
  ANALYSIS: 'Certificate of analysis',
  OTHER: 'Other',
};

const KIND_LABEL: Record<string, string> = {
  COMPONENT: 'Component',
  STANDARD_PART: 'Standard part',
  RAW_MATERIAL: 'Raw material',
  CONSUMABLE: 'Consumable',
};

const RELEASE_LABEL: Record<string, string> = {
  EASA_FORM_1: 'EASA Form 1',
  FAA_8130_3: 'FAA 8130-3',
  DUAL_RELEASE: 'Dual release',
  CoC: 'Certificate of conformity',
  CoA: 'Certificate of analysis',
};

/** The poll that follows a reading. Held here so leaving the screen stops it. */
let watching: ReturnType<typeof setTimeout> | null = null;
/**
 * The clock on the waiting panel, separate from the poll.
 *
 * A screen that only moved when the poll came back would sit still for six
 * seconds at a time and read as frozen, and polling every second to make it
 * move would be six times the requests for a number the browser already knows.
 */
let ticking: ReturnType<typeof setInterval> | null = null;
const stopWatching = () => {
  if (watching) clearTimeout(watching);
  if (ticking) clearInterval(ticking);
  watching = null;
  ticking = null;
};

const money = (n: number | null, currency: string | null) =>
  n === null ? '—' : `${n.toLocaleString('en-GB', { minimumFractionDigits: 2 })} ${currency ?? ''}`.trim();

/** Where a field was read from, in the words of the document itself. */
function source(lot: StockImportLot, fieldName: string): string {
  if (lot.needsConfirming.includes(fieldName)) {
    return '<span class="fl fl-own">confirm — the papers do not say</span>';
  }
  const hit = lot.sources.find((s) => s.field === fieldName);
  if (!hit) return '';
  // A value somebody typed is not a value the invoice says, and the two must
  // not look alike on the screen.
  if (hit.file === 'entered by hand') return '<span class="fl fl-have">entered by hand</span>';
  const where = hit.page ? `${hit.file} · p.${hit.page}` : hit.file;
  return `<span class="fl fl-ref">${esc(where)}</span>`;
}

function lotRow(lot: StockImportLot): string {
  const rejected = lot.decision === 'REJECTED';
  const line = (label: string, value: string, fieldName: string) => html`
    <div class="field">
      <span class="lbl">${esc(label)}</span>
      <div>
        <div class="val">${value}</div>
        <div class="prov">${source(lot, fieldName)}</div>
      </div>
    </div>
  `;

  return html`
    <div class="card ${rejected ? 'is-rejected' : ''}" data-lot="${esc(lot.id)}">
      <div class="card-h">
        <div>
          <h2>${esc(lot.description)}</h2>
          <p class="tiny dim">
            <span class="mono">${esc(lot.partNumber)}</span>
            ${lot.batchLot ? html` · batch ${esc(lot.batchLot)}` : ''}
            · ${esc(KIND_LABEL[lot.kind] ?? lot.kind)}
            · ${lot.quantity} ${esc(lot.unit)}
          </p>
        </div>
        <div style="display:flex;gap:6px;align-items:center">
          ${lot.needsConfirming.length
            ? pill('DUE_SOON', `${lot.needsConfirming.length} to confirm`)
            : pill('OK', 'nothing outstanding')}
          <button class="btn sm" data-edit="${esc(lot.id)}">Correct</button>
          <button class="btn sm" data-toggle="${esc(lot.id)}">${rejected ? 'Receive it after all' : 'Do not receive'}</button>
        </div>
      </div>
      <div class="cols2" style="padding:12px 14px">
        <div>
          ${line('Part number', esc(lot.partNumber), 'partNumber')}
          ${line('Batch / lot', esc(lot.batchLot ?? '—'), 'batchLot')}
          ${line('Serial number', esc(lot.serialNumber ?? '—'), 'serialNumber')}
          ${line('Quantity', `${lot.quantity} ${esc(lot.unit)}`, 'quantity')}
          ${line('Manufacturer', esc(lot.manufacturer ?? '—'), 'manufacturer')}
        </div>
        <div>
          ${line('Supplier', esc(lot.supplier ?? '—'), 'supplier')}
          ${line('Manufactured', esc(fmtDate(lot.manufacturedAt)), 'manufacturedAt')}
          ${line('Shelf life', esc(fmtDate(lot.shelfLifeExpiry)), 'shelfLifeExpiry')}
          ${line('Cost', esc(money(lot.unitCost, lot.currency)), 'unitCost')}
          ${line(
            'Release',
            lot.releaseKind
              ? `${esc(RELEASE_LABEL[lot.releaseKind] ?? lot.releaseKind)}${lot.releaseReference ? ` · ${esc(lot.releaseReference)}` : ''}`
              : '—',
            'releaseKind',
          )}
        </div>
      </div>
      ${lot.notes ? html`<div class="card-f">${esc(lot.notes)}</div>` : ''}
      <div class="card-f">
        Filled by FlyPack CAMO, not by the reading:
        <span class="fl fl-have">received today</span>
        <span class="fl fl-have">quarantine</span>
        ${lot.filledByUs.salePrice !== null
          ? html`<span class="fl fl-have">sale price ${esc(money(lot.filledByUs.salePrice, lot.filledByUs.salePriceCurrency))} — cost + ${lot.filledByUs.markupPercent}%</span>`
          : lot.filledByUs.markupPercent === null
            ? html`<span class="fl fl-own">no sale price — no markup is set on the organisation</span>`
            : html`<span class="fl fl-own">no sale price — the rate for ${esc(lot.currency ?? 'this currency')} is missing</span>`}
      </div>
    </div>
  `;
}

/**
 * Every delivery that has been read.
 *
 * Without it a reading was reachable for as long as the tab stayed open and no
 * longer: what the store holds is on the shelf, but *why* it holds it — which
 * invoice, read when, and what a person changed before accepting it — had no
 * way back to it.
 */
function historyCard(past: StockImportSummary[]): string {
  if (!past.length) return '';
  const STATE: Record<string, { tone: State; label: string }> = {
    RECEIVED: { tone: 'OK', label: 'Received' },
    READY: { tone: 'DUE_SOON', label: 'Waiting to be received' },
    READING: { tone: 'DUE_SOON', label: 'Being read' },
    FAILED: { tone: 'OVERDUE', label: 'Did not finish' },
    ABANDONED: { tone: 'NA', label: 'Abandoned' },
  };

  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>Deliveries read before</h2>
          <p class="tiny dim">What each one produced, and what it cost to read</p>
        </div>
      </div>
      <div class="tblwrap">
        <table class="tbl">
          <thead>
            <tr>
              <th>Read</th><th class="wide">Supplier and invoice</th>
              <th>Papers</th><th class="col-opt">Lots</th><th>Outcome</th><th></th>
            </tr>
          </thead>
          <tbody>
            ${past.map((p) => {
              const s = STATE[p.state] ?? { tone: 'NA' as State, label: p.state };
              return html`
                <tr>
                  <td class="tiny">${esc(fmtDate(p.startedAt))}</td>
                  <td class="wide">
                    ${esc(p.supplier ?? 'supplier not read')}
                    <span class="sub2">
                      ${p.invoiceReference ? `invoice ${esc(p.invoiceReference)}` : 'no invoice reference'}
                      ${p.totalExVat !== null ? ` · ${esc(money(p.totalExVat, p.currency))}` : ''}
                    </span>
                    ${p.failure ? `<span class="sub2" style="color:var(--exceed)">${esc(p.failure)}</span>` : ''}
                  </td>
                  <td class="tiny dim">${p.documents.length}</td>
                  <td class="col-opt tiny">
                    ${p.totals.received
                      ? `${p.totals.received} received`
                      : `${p.totals.proposed} proposed`}
                  </td>
                  <td>${pill(s.tone, s.label)}</td>
                  <td><button class="btn sm" data-open-import="${esc(p.id)}">Open</button></td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
      <div class="card-f">
        A reading is not an import: what is listed here is what the model proposed and what a person
        then accepted. The lots it created are in the store, with the papers filed against each.
      </div>
    </div>
  `;
}

function pickerView(ctx: StockImportContext, last: StockImport | null, past: StockImportSummary[]): string {
  const inFlight = last && last.state === 'READING';
  const failed = last?.state === 'FAILED';
  return html`
    <div class="page-h">
      <div>
        <p class="tiny dim"><a href="/stores">← Stores</a></p>
        <h1>Receive from the paperwork</h1>
        <p class="dim">
          The invoice and the certificate of conformity are enough. Everything else fills in what
          those two do not carry.
        </p>
      </div>
      <button class="btn pri" data-start>Read these papers</button>
    </div>

    <div data-reading>${inFlight ? readingCallout(last!) : ''}</div>

    ${failed
      ? callout('exceed', '!', html`
          <b>The last reading did not finish.</b><br>
          ${esc(last!.failure ?? 'No reason was recorded.')}
          <br><br>
          <button class="btn sm" data-abandon>Clear it and start again</button>
        `)
      : ''}

    ${!ctx.configured
      ? callout('caution', '!', html`
          <b>No key is configured on this installation</b>, so the papers cannot be read. Stock can
          still be received by hand from the Stores screen.
        `)
      : ''}

    ${!ctx.hasLocations
      ? callout('caution', '!', html`
          <b>This store has no locations yet.</b> A received lot goes to a quarantine bin, and there
          is none to put it in — add one on the Stores screen first.
        `)
      : ''}

    <div class="card">
      <div class="card-h">
        <div>
          <h2>The papers that came with the delivery</h2>
          <p class="tiny dim">They are filed in the document library either way, and linked to what they produce</p>
        </div>
      </div>
      <div class="card-b">
        <div class="pick" data-files></div>
        <div style="margin-top:10px">
          <input type="file" accept="application/pdf" multiple data-file-input style="display:none">
          <button class="btn sm" data-add-file>Add a document</button>
          <span class="tiny dim" style="margin-left:8px">PDF, up to six</span>
        </div>
      </div>
      <div class="card-f">
        What each one adds: the <b>invoice</b> carries the price, the quantity and who sold it; the
        <b>certificate of conformity</b> carries the batch, the manufacture date and the shelf life;
        an <b>8130-3 or Form 1</b> carries the release the component needs.
      </div>
    </div>

    ${historyCard(past)}
  `;
}

/**
 * How long these usually take. Not a deadline — what the bar is measured
 * against, so that "longer than usual" is something the screen can say rather
 * than something the reader has to wonder about.
 */
const TYPICAL_SECONDS = 35;

const secondsSince = (iso: string) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));

/** Short of the end, always: the reading reports no percentage, so nor does this. */
const readingProgress = (seconds: number) => Math.min(94, Math.round((seconds / TYPICAL_SECONDS) * 100));

const readingElapsed = (seconds: number) =>
  seconds > TYPICAL_SECONDS * 2
    ? `${seconds}s so far — longer than usual, but a reading is not stuck until it has been quiet for twenty minutes`
    : `${seconds}s so far · these usually take about half a minute`;

function readingCallout(e: StockImport): string {
  const seconds = secondsSince(e.startedAt);
  const pages = e.documents.reduce((n, d) => n + (d.pages ?? 0), 0);
  return callout('info', 'i', html`
    <b>Reading ${e.documents.length || e.totals.documents} document(s)${pages ? ` · ${pages} page(s)` : ''}.</b>
    This page keeps itself up to date, so it can be left open — or closed and come back to.
    <div class="prog"><span data-bar style="width:${readingProgress(seconds)}%"></span></div>
    <div class="tiny dim" data-elapsed>${esc(readingElapsed(seconds))}</div>
    ${e.documents.length
      ? html`<div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:8px">
          ${e.documents.map((d) => html`
            <span class="fl fl-ref">${esc(d.fileName)} · ${esc(ROLE_LABEL[d.role] ?? d.role)}${
              d.pages ? ` · ${d.pages} p.` : ''
            }</span>
          `).join('')}
        </div>`
      : ''}
    <br>
    <button class="btn sm" data-abandon>Abandon the reading</button>
  `);
}

/**
 * The one number nobody can read off the paperwork.
 *
 * Only shown where the invoice is in money the organisation does not bill in.
 * Until it is filled in the lots have no sale price at all — which is the
 * honest outcome, because a rate the application invented would reach an
 * invoice looking exactly like one somebody checked.
 */
function rateCard(e: StockImport, open: boolean): string {
  const from = e.delivery.currency;
  const to = e.billingCurrency;
  if (!from || !to || from === to) return '';

  const example = e.lots.find((l) => l.unitCost !== null);
  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>What the money is worth</h2>
          <p class="tiny dim">
            This delivery is invoiced in ${esc(from)}; the organisation bills in ${esc(to)}
          </p>
        </div>
        ${e.exchangeRate !== null ? pill('OK', 'rate on file') : pill('DUE_SOON', 'no rate yet')}
      </div>
      <div class="card-b">
        <div class="frow3">
          ${field(
            `1 ${from} in ${to}`,
            input('exchangeRate', e.exchangeRate === null ? '' : String(e.exchangeRate),
              `inputmode="decimal" style="text-align:right;font-family:var(--mono)" ${open ? '' : 'disabled'}`),
            'The rate on the invoice date, as your accounts use it',
          )}
          <div class="field">
            <label>What that makes of it</label>
            <div class="v">
              ${example && e.exchangeRate !== null
                ? html`${esc(money(example.unitCost, from))} → <b>${esc(money(
                    Math.round((example.unitCost as number) * e.exchangeRate * 100) / 100, to,
                  ))}</b> <span class="tiny dim">before the markup</span>`
                : '<span class="dim">—</span>'}
            </div>
          </div>
          <div class="field">
            <label></label>
            <div>${open ? '<button class="btn" data-save-rate>Save the rate</button>' : ''}</div>
          </div>
        </div>
      </div>
      <div class="card-f">
        Nothing is converted at a rate nobody supplied. Left empty, these lots reach the shelf with
        no sale price — the store will hold them and the release will refuse to bill them, which is
        louder than a wrong figure and easier to put right.
      </div>
    </div>
  `;
}

function reviewView(e: StockImport): string {
  const received = e.state === 'RECEIVED';
  // Only a reading still waiting can be received or abandoned. Opened out of
  // the history, one that failed or was abandoned is a record to look at.
  const open = e.state === 'READY';
  const accepted = e.lots.filter((l) => l.decision === 'ACCEPTED');
  return html`
    <div class="page-h">
      <div>
        <p class="tiny dim"><a href="/stores">← Stores</a></p>
        <h1>${received ? 'Received from the paperwork' : open ? 'Check what was read' : 'A reading that went nowhere'}</h1>
        <p class="dim">
          ${e.delivery.supplier ? html`${esc(e.delivery.supplier)} · ` : ''}
          ${e.delivery.invoiceReference ? html`invoice ${esc(e.delivery.invoiceReference)} · ` : ''}
          ${e.delivery.invoiceDate ? html`${esc(fmtDate(e.delivery.invoiceDate))} · ` : ''}
          ${e.totals.proposed} lot(s) from ${e.totals.documents} document(s)
        </p>
        <p class="tiny dim">
          Read by ${esc(e.model)} · ${e.totals.tokensIn.toLocaleString('en-GB')} tokens in,
          ${e.totals.tokensOut.toLocaleString('en-GB')} out
        </p>
      </div>
      <div style="display:flex;gap:8px;align-items:center">
        ${pill(
          received ? 'OK' : open ? 'DUE_SOON' : 'NA',
          received ? 'Received' : open ? 'Not yet received' : e.state === 'FAILED' ? 'Did not finish' : 'Abandoned',
        )}
        ${open ? '<button class="btn" data-abandon>Abandon</button>' : ''}
        ${open ? `<button class="btn pri" data-receive>Receive ${accepted.length} lot(s)</button>` : ''}
        ${received || !open ? '<a class="btn" href="/stores/import">Read another delivery</a>' : ''}
      </div>
    </div>

    ${e.warnings.map((w) => callout('caution', '!', esc(w))).join('')}

    ${rateCard(e, open)}

    ${received
      ? callout('ok', '✓', html`
          These lots are in the store, in quarantine, awaiting incoming inspection — and both papers
          are filed against each of them.
        `)
      : ''}

    ${!received && !open
      ? callout('exceed', '!', html`
          <b>Nothing was received from this reading.</b>
          ${esc(e.failure ?? 'It was abandoned before anybody accepted it.')}
          The papers themselves are still in the document library.
        `)
      : ''}

    <div class="card">
      <div class="card-h">
        <div>
          <h2>What was read</h2>
          <p class="tiny dim">Each one is filed in the document library and linked to every lot it covers</p>
        </div>
      </div>
      <div class="rows">
        ${e.documents.map((d) => html`
          <div class="row">
            <div class="bd">
              <div class="t">
                ${d.attachmentId
                  ? html`<button class="docopen" data-doc="${esc(d.attachmentId)}" data-title="${esc(d.title)}">${esc(d.title)}</button>`
                  : esc(d.title)}
              </div>
              <div class="m tiny dim">${esc(d.fileName)} · ${esc(ROLE_LABEL[d.role] ?? d.role)}</div>
            </div>
          </div>
        `).join('')}
      </div>
    </div>

    ${e.lots.map((l) => lotRow(l)).join('')}
  `;
}

/**
 * @param openId a particular reading to show — one just received, or one
 *   picked out of the history. Without it the screen shows whatever is in
 *   flight, and the picker when nothing is.
 */
export async function renderStockImport(host: HTMLElement, openId?: string): Promise<void> {
  stopWatching();
  host.innerHTML = loading('Loading');
  // Files chosen but not yet sent. Held here rather than read from the input,
  // which cannot be added to across two clicks.
  const chosen: Array<{ file: File; role: string }> = [];

  try {
    const [ctx, latest, past] = await Promise.all([
      api.stockImports.context(),
      openId ? Promise.resolve(null) : api.stockImports.latest(),
      api.stockImports.history(),
    ]);
    const existing = openId ? await api.stockImports.get(openId) : latest;
    const reload = () => void renderStockImport(host, openId);
    const home = () => void renderStockImport(host);

    if (existing && ['READY', 'RECEIVED', 'FAILED', 'ABANDONED'].includes(existing.state)) {
      host.innerHTML = reviewView(existing);
      bindReview(host, existing, openId ? home : reload);
      return;
    }

    host.innerHTML = pickerView(ctx, existing, past);
    renderChosen();

    host.querySelectorAll<HTMLElement>('[data-open-import]').forEach((b) =>
      b.addEventListener('click', () => void renderStockImport(host, b.dataset.openImport!)),
    );

    if (existing && existing.state === 'READING') {
      const tick = async () => {
        if (!host.isConnected) return;
        try {
          const now = await api.stockImports.latest();
          if (!now || now.state !== 'READING') return reload();
          const box = host.querySelector('[data-reading]');
          if (box) box.innerHTML = readingCallout(now);
        } catch {
          // The next poll is six seconds away; the reading is unaffected.
        }
        watching = setTimeout(tick, 6_000);
      };
      watching = setTimeout(tick, 6_000);

      // The seconds, moved by the browser. Only the two elements that say
      // anything are rewritten, so nothing under the cursor flickers.
      const startedAt = existing.startedAt;
      ticking = setInterval(() => {
        if (!host.isConnected) return stopWatching();
        const seconds = secondsSince(startedAt);
        const bar = host.querySelector<HTMLElement>('[data-bar]');
        const said = host.querySelector<HTMLElement>('[data-elapsed]');
        if (bar) bar.style.width = `${readingProgress(seconds)}%`;
        if (said) said.textContent = readingElapsed(seconds);
      }, 1_000);
    }

    function renderChosen() {
      const box = host.querySelector<HTMLElement>('[data-files]');
      if (!box) return;
      box.innerHTML = chosen.length
        ? chosen.map((c, i) => html`
            <label>
              <span class="pb">
                <span class="pt">${esc(c.file.name)}</span>
                <span class="pm">${(c.file.size / 1e6).toFixed(1)} MB</span>
              </span>
              <span style="display:flex;gap:6px;align-items:center">
                <select data-role="${i}" class="sel sm">
                  ${['INVOICE', 'CONFORMITY', 'RELEASE', 'ANALYSIS', 'OTHER']
                    .map((r) => opt(r, ROLE_LABEL[r], r === c.role)).join('')}
                </select>
                <button class="btn sm" data-drop="${i}">Remove</button>
              </span>
            </label>
          `).join('')
        : html`<label><span class="pb">
            <span class="pt">Nothing chosen yet</span>
            <span class="pm">The invoice and the certificate of conformity are the two that matter.</span>
          </span></label>`;

      box.querySelectorAll<HTMLSelectElement>('[data-role]').forEach((sel) =>
        sel.addEventListener('change', () => { chosen[Number(sel.dataset.role)].role = sel.value; }),
      );
      box.querySelectorAll<HTMLElement>('[data-drop]').forEach((b) =>
        b.addEventListener('click', () => { chosen.splice(Number(b.dataset.drop), 1); renderChosen(); }),
      );
    }

    const fileInput = host.querySelector<HTMLInputElement>('[data-file-input]');
    host.querySelector('[data-add-file]')?.addEventListener('click', () => fileInput?.click());
    fileInput?.addEventListener('change', () => {
      for (const file of Array.from(fileInput.files ?? [])) {
        if (chosen.length >= 6) break;
        // A guess from the name, corrected in one click if it is wrong. Most
        // deliveries name their papers after what they are.
        const name = file.name.toLowerCase();
        const role = /factur|invoice|fplts/.test(name) ? 'INVOICE'
          : /8130|form.?1/.test(name) ? 'RELEASE'
            : /conformit|coc|certificat/.test(name) ? 'CONFORMITY'
              : /analys|analiz|coa/.test(name) ? 'ANALYSIS' : 'OTHER';
        chosen.push({ file, role });
      }
      fileInput.value = '';
      renderChosen();
    });

    host.addEventListener('click', async (ev) => {
      if (!(ev.target as HTMLElement).closest('[data-abandon]')) return;
      const running = await api.stockImports.latest();
      if (!running) return reload();
      try {
        await api.stockImports.abandon(running.id, 'Cleared from the start screen.');
        toast('Cleared. You can read the papers again.');
      } catch (err) {
        toast(err instanceof Error ? err.message : 'That could not be cleared.', 'error');
      }
      reload();
    });

    host.querySelector('[data-start]')?.addEventListener('click', async () => {
      if (!chosen.length) return toast('Add the invoice, or the certificate, or both.', 'error');
      const button = host.querySelector<HTMLButtonElement>('[data-start]')!;
      button.disabled = true;
      button.textContent = 'Starting…';
      try {
        const form = new FormData();
        for (const c of chosen) {
          form.append('files', c.file, c.file.name);
          form.append('roles', c.role);
        }
        const started = await api.stockImports.start(form);
        toast(`Reading ${started.documents} document(s) — this page will show what it found.`);
        reload();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'The papers could not be read.', 'error');
        button.disabled = false;
        button.textContent = 'Read these papers';
      }
    });
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load this screen.');
  }
}

function bindReview(host: HTMLElement, e: StockImport, reload: () => void): void {
  host.querySelectorAll<HTMLElement>('[data-doc]').forEach((b) =>
    b.addEventListener('click', () =>
      openPdfViewer({
        path: `/attachments/${b.dataset.doc}/download`,
        title: b.dataset.title ?? 'Document',
        subtitle: 'Filed with this delivery',
        accessToken: getAccessToken,
      }),
    ),
  );

  host.querySelectorAll<HTMLElement>('[data-toggle]').forEach((b) =>
    b.addEventListener('click', async () => {
      const lot = e.lots.find((l) => l.id === b.dataset.toggle);
      if (!lot) return;
      try {
        await api.stockImports.updateLot(e.id, lot.id, {
          decision: lot.decision === 'REJECTED' ? 'ACCEPTED' : 'REJECTED',
        });
        reload();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'That could not be changed.', 'error');
      }
    }),
  );

  host.querySelectorAll<HTMLElement>('[data-edit]').forEach((b) =>
    b.addEventListener('click', () => {
      const lot = e.lots.find((l) => l.id === b.dataset.edit);
      if (lot) editLot(e, lot, reload);
    }),
  );

  host.querySelector('[data-abandon]')?.addEventListener('click', () => {
    openDialog<Record<string, unknown>>({
      title: 'Abandon this reading',
      sub: 'The papers stay in the document library; nothing is received',
      primary: 'Abandon it',
      body: () => field('Why', input('reason', '', 'maxlength="400"')),
      collect: (root) => {
        const reason = val(root, 'reason');
        return reason ? { reason } : { error: 'Say why it was abandoned.' };
      },
      submit: (body) => api.stockImports.abandon(e.id, String(body.reason)),
      done: () => 'Abandoned',
      onDone: reload,
    });
  });

  host.querySelector('[data-save-rate]')?.addEventListener('click', async () => {
    const box = host.querySelector<HTMLInputElement>('[name="exchangeRate"]');
    const raw = box?.value.trim() ?? '';
    const rate = raw === '' ? null : Number(raw);
    if (rate !== null && !(rate > 0)) {
      return toast('A rate is a number greater than zero — or empty, for none.', 'error');
    }
    try {
      await api.stockImports.setRate(e.id, rate);
      toast(rate === null ? 'Rate cleared — these lots will have no sale price.' : 'Rate saved.');
      reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'That rate could not be saved.', 'error');
    }
  });

  host.querySelector('[data-receive]')?.addEventListener('click', () => {
    const accepted = e.lots.filter((l) => l.decision === 'ACCEPTED');
    const confirming = accepted.reduce((n, l) => n + l.needsConfirming.length, 0);
    openDialog<Record<string, unknown>>({
      wide: true,
      title: `Receive ${accepted.length} lot(s)`,
      sub: 'This is the point at which the store changes',
      primary: 'Receive them',
      body: () => html`
        <div class="rows">
          ${accepted.map((l) => html`
            <div class="row">
              <div class="bd">
                <div class="t">${esc(l.description)}</div>
                <div class="m tiny dim">
                  <span class="mono">${esc(l.partNumber)}</span>
                  ${l.batchLot ? ` · batch ${esc(l.batchLot)}` : ''} · ${l.quantity} ${esc(l.unit)}
                  · ${esc(money(l.unitCost, l.currency))}
                </div>
              </div>
              ${pill('DUE_SOON', 'Quarantine')}
            </div>
          `).join('')}
        </div>
        ${confirming
          ? callout('caution', '!', html`
              <b>${confirming} field(s) are still marked "confirm".</b> The papers do not state them
              — for oil there is usually no part number printed anywhere. Correct them first if they
              matter, because a lot is found by its part number later.
            `)
          : ''}
        ${callout('info', 'i', html`
          Every lot arrives in <b>quarantine</b> and cannot be issued until somebody signs the
          incoming inspection — 145.A.42. Both papers are filed against each lot.
        `)}
      `,
      collect: () => ({}),
      submit: () => api.stockImports.receive(e.id),
      done: (r) => `${r.received} lot(s) received · ${r.documentsLinked} document(s) filed with each`,
      // Straight into the labels. What has just been received is in a
      // quarantine bin needing something on the box, and the alternative was
      // finding the same lots again on another screen and ticking them.
      onDone: (r: { lots: Array<{ lotId: string }> }) => {
        reload();
        if (r.lots?.length) printLabels(r.lots.map((l) => l.lotId));
      },
    });
  });
}

/** Correcting what was read, one lot at a time. */
function editLot(e: StockImport, lot: StockImportLot, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Correct what was read',
    sub: `${lot.description} — the papers are on the screen behind this`,
    primary: 'Save the correction',
    body: () => html`
      <div class="frow3">
        ${field('Part number', input('partNumber', lot.partNumber, 'maxlength="60" style="font-family:var(--mono)"'))}
        ${field('Batch / lot', input('batchLot', lot.batchLot ?? '', 'maxlength="60" style="font-family:var(--mono)"'))}
        ${field('Serial number', input('serialNumber', lot.serialNumber ?? '', 'maxlength="60" style="font-family:var(--mono)"'))}
      </div>
      ${field('Description', input('description', lot.description, 'maxlength="200"'))}
      <div class="frow3">
        ${field('Kind', select('kind', ['COMPONENT', 'STANDARD_PART', 'RAW_MATERIAL', 'CONSUMABLE']
          .map((k) => opt(k, KIND_LABEL[k], k === lot.kind)).join('')))}
        ${field('Quantity', input('quantity', lot.quantity, 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
        ${field('Unit', select('unit', ['EA', 'L', 'ML', 'KG', 'G', 'M', 'SET']
          .map((u) => opt(u, u, u === lot.unit)).join('')))}
      </div>
      <div class="frow">
        ${field('Manufacturer', input('manufacturer', lot.manufacturer ?? '', 'maxlength="160"'))}
        ${field('Supplier', input('supplier', lot.supplier ?? '', 'maxlength="160"'))}
      </div>
      <div class="frow3">
        ${field('Manufactured', input('manufacturedAt', lot.manufacturedAt?.slice(0, 10) ?? '', 'type="date"'))}
        ${field('Shelf life expiry', input('shelfLifeExpiry', lot.shelfLifeExpiry?.slice(0, 10) ?? '', 'type="date"'))}
        ${field('Cost', input('unitCost', lot.unitCost ?? '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
      </div>
      <div class="frow3">
        ${field('Released on', select('releaseKind', [
          opt('', 'None'),
          ...['EASA_FORM_1', 'FAA_8130_3', 'DUAL_RELEASE', 'CoC', 'CoA']
            .map((r) => opt(r, RELEASE_LABEL[r], r === lot.releaseKind)),
        ].join('')))}
        ${field('Reference', input('releaseReference', lot.releaseReference ?? '', 'maxlength="60" style="font-family:var(--mono)"'))}
        ${field('Issued by', input('releaseIssuer', lot.releaseIssuer ?? '', 'maxlength="160"'))}
      </div>
      ${callout('info', 'i', html`
        What the store will not accept is refused when the lot is received, not here: a component
        cannot be received on a certificate of conformity, and a consumable cannot be received on a
        Form 1 — 145.A.42(a).
      `)}
    `,
    collect: (root) => {
      const partNumber = val(root, 'partNumber').trim();
      if (!partNumber) return { error: 'A lot is found by its part number. It needs one.' };
      const quantity = Number(val(root, 'quantity'));
      if (!(quantity > 0)) return { error: 'A receipt has to be for more than nothing.' };
      return {
        partNumber,
        description: val(root, 'description').trim() || partNumber,
        kind: val(root, 'kind'),
        batchLot: val(root, 'batchLot').trim() || null,
        serialNumber: val(root, 'serialNumber').trim() || null,
        quantity,
        unit: val(root, 'unit'),
        manufacturer: val(root, 'manufacturer').trim() || null,
        supplier: val(root, 'supplier').trim() || null,
        manufacturedAt: val(root, 'manufacturedAt') || null,
        shelfLifeExpiry: val(root, 'shelfLifeExpiry') || null,
        unitCost: val(root, 'unitCost') ? Number(val(root, 'unitCost')) : null,
        releaseKind: val(root, 'releaseKind') || null,
        releaseReference: val(root, 'releaseReference').trim() || null,
        releaseIssuer: val(root, 'releaseIssuer').trim() || null,
      };
    },
    submit: (body) => api.stockImports.updateLot(e.id, lot.id, body),
    done: () => 'Corrected',
    onDone: reload,
  });
}
